<script
  setup
  lang="ts"
  generic="T, M extends boolean, Value extends M extends true ? (string[] | DataItem<string>[] | null | undefined) : T"
>
import { isEqualItem } from "../../helpers.js";
import type { DataItem, DropdownItem, UIColor } from "../../..";
import { computed, provide, ref, useTemplateRef } from "vue";
import Anchored from "../Anchored/Anchored.vue";
import Icon from "../Icon/Icon.vue";

const props = defineProps<{
  label: "--multiple-default" | "--multiple-one-max" | string;
  placeholder?: string;
  placeholderMultiple?: string;
  colorBackground: UIColor;
  colorForeground: UIColor;
  colorBorder?: string | undefined;
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  multiple?: M;
  clearButton?: boolean;
}>();

const multiple = computed(() =>
  "multiple" in props
    ? props.multiple === true || props.multiple !== undefined
    : false
);

const model = defineModel<Value>();

const items = ref<DropdownItem<Value>[]>([]);

provide("model", model);
provide("multiple", !!multiple.value);
provide("items", items);
provide("colorForeground", props.colorForeground);

const getLabel = (value: T) => {
  return (
    items.value.find((item) => isEqualItem(value, item.value))?.label ?? ""
  );
};

const multiplelabel = computed(() => {
  const values = model.value as T[] | undefined;

  switch (props.label) {
    case "--multiple-default":
      return values?.length
        ? values.map(getLabel).join(", ")
        : props.placeholder ?? "–";
    case "--multiple-one-max":
      return values?.length
        ? values.length === 1
          ? getLabel(values[0])
          : props.placeholderMultiple ?? "+"
        : props.placeholder ?? "–";
    default:
      return props.label;
  }
});

const clear = () => (model.value = undefined);

const labelbutton = useTemplateRef("labelbutton");
const select = ref<HTMLSelectElement | null>(null);

const popoverOpen = ref(false);

const showPopover = () => {
  if (!popoverOpen.value) popoverOpen.value = true;
};

const onDropdownClick = () => showPopover();
</script>

<template>
  <div
    :class="$style.container"
    :style="{
      '--drop-down-color-background': `var(${colorBackground})`,
      '--drop-down-color-foreground': `var(${colorForeground})`,
      '--drop-down-color-border': `var(${colorBorder})`,
      'min-width': minWidth,
      'max-width': maxWidth,
    }"
  >
    <select
      v-if="!multiple"
      ref="select"
      v-model="model"
      :class="$style.select"
    >
      <slot />
    </select>

    <div
      ref="labelbutton"
      @click.stop="onDropdownClick"
      :class="[$style.label, multiple && $style.multiple]"
    >
      <div :class="$style.ellipsis">
        {{ multiple ? multiplelabel : label }}
      </div>

      <div :class="$style.buttons">
        <Icon
          v-if="clearButton && !!model"
          id="close"
          size="medium"
          :color="colorForeground"
          :class="$style.clear"
          @click.stop.prevent="clear"
        />

        <Icon id="arrow_drop_down" size="large" :color="colorForeground" />
      </div>
    </div>

    <Anchored
      v-if="multiple"
      ref="anchored"
      :anchor="labelbutton"
      :class="$style.dropdown"
      :positionOnAnchor="['left', ({ anchor }) => anchor.bottom + 1]"
      :positionSelf="['left', 'top']"
      :style="{ minWidth: `calc(${minWidth} + 0.75rem)`, maxWidth: maxWidth }"
      v-model:visible="popoverOpen"
    >
      <div :class="$style.dropdownContent" :style="{ 'max-height': maxHeight }">
        <slot />
      </div>
    </Anchored>
  </div>
</template>

<style module>
.container {
  display: inline-flex;
  position: relative;
  min-height: 2.5rem;
  align-items: center;
  padding-right: 0;
}

.select {
  appearance: none;
  outline: none;
  display: inline-flex;
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-width: inherit;
  max-width: inherit;
  min-height: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  padding-left: 1rem;
  color: var(--color-primary);
  font-family: "Albert Sans";
  border-style: none;
  background-color: var(--color-transparent);
  opacity: 0.01;
}

.label {
  position: absolute;
  width: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: inline-flex;
  align-content: flex-end;
  align-items: center;
  padding-left: 0.75rem;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: var(--drop-down-color-foreground);
  background-color: var(--drop-down-color-background);
  border: 1px solid var(--drop-down-color-border);
  user-select: none;
  pointer-events: none;
  z-index: 1;
}

.label.multiple {
  pointer-events: auto;
}

.dropdown {
  padding: 0;
  margin: 0;
  background-color: var(--color-white);
  box-shadow: var(--box-shadow-medium);
  border: none;
}

.dropdownContent {
  display: flex;
  gap: 0.25rem;
  flex-direction: column;
  width: calc(100% - 1rem);
  overflow: auto;
  max-height: 20rem;
  padding: 0.5rem;
}

.ellipsis {
  display: inline;
  flex: 1;
  white-space: nowrap;
  margin-right: -0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "Albert Sans";
}

.buttons {
  display: inline-flex;
  gap: 0.25rem;
  padding-left: 0.25rem;
  padding-right: 0.5rem;
  align-items: center;
  pointer-events: none;
}

.clear {
  pointer-events: auto;
  cursor: pointer;
}

.clear:hover {
  opacity: 0.5;
}
</style>
